import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PublicClientApplication, AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { msalConfig, apiRequest } from '../../config/authConfig';
import { setAuthToken, clearAuthToken } from '../../api/apiClient';
import { msalInstance } from '../../config/msalInstance';
// Types
export interface AuthState {
    isAuthenticated: boolean;
    account: AccountInfo | null;
    username: string | null;
    accessToken: string | null;
    loading: boolean;
    error: string | null;
}


// Initial state
const initialState: AuthState = {
    isAuthenticated: false,
    account: null,
    username: null,
    accessToken: null,
    loading: false,
    error: null,
};

// Async thunks
export const initializeAuth = createAsyncThunk(
    'auth/initialize',
    async (_, { dispatch }) => {
        // Load accounts from cache
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length > 0) {
            // Use the first account if available
            const account = accounts[0];

            // Silently acquire token
            try {
                const response = await msalInstance.acquireTokenSilent({
                    ...apiRequest,
                    account,
                });

                // Set the auth token for API requests
                setAuthToken(response.accessToken);

                return {
                    account,
                    accessToken: response.accessToken,
                    username: account.username || account.name || 'User',
                };
            } catch (error) {
                console.error('Silent token acquisition failed', error);
                return null;
            }
        }

        return null;
    }
);

export const login = createAsyncThunk(
    'auth/login',
    async (_, { rejectWithValue }) => {
        try {
            // Try silent authentication first
            const accounts = msalInstance.getAllAccounts();

            if (accounts.length > 0) {
                try {
                    const response = await msalInstance.acquireTokenSilent({
                        ...apiRequest,
                        account: accounts[0],
                    });

                    // Set the auth token for API requests
                    setAuthToken(response.accessToken);

                    return {
                        account: accounts[0],
                        accessToken: response.accessToken,
                        username: accounts[0].username || accounts[0].name || 'User',
                    };
                } catch (silentError) {
                    console.error('Silent token acquisition failed', silentError);
                    // Fall back to redirect flow
                }
            }

            // Use popup login flow — redirect popup to blank.html so the full app
            // doesn't load inside the popup window (which would show the login page again)
            const response = await msalInstance.loginPopup({
                ...apiRequest,
                redirectUri: `${window.location.origin}/blank.html`,
            });

            // Set the auth token for API requests
            setAuthToken(response.accessToken);

            // Check if account exists before accessing its properties
            if (response.account) {
                return {
                    account: response.account,
                    accessToken: response.accessToken,
                    username: response.account.username || response.account.name || 'User',
                };
            } else {
                // Fallback if no account info is available
                return {
                    account: null,
                    accessToken: response.accessToken,
                    username: 'User',
                };
            }
        } catch (error: any) {
            console.error('Login failed', error);
            return rejectWithValue(error.message || 'Login failed');
        }
    }
);

export const logout = createAsyncThunk(
    'auth/logout',
    async (_, { rejectWithValue }) => {
        try {
            // Clear API auth token
            clearAuthToken();

            await msalInstance.logoutPopup({
                postLogoutRedirectUri: `${window.location.origin}/blank.html`,
            });

            return true;
        } catch (error: any) {
            console.error('Logout failed', error);
            return rejectWithValue(error.message || 'Logout failed');
        }
    }
);

export const acquireToken = createAsyncThunk(
    'auth/acquireToken',
    async (_, { getState, rejectWithValue }) => {
        const state = getState() as { auth: AuthState };

        if (!state.auth.account) {
            return rejectWithValue('No account found');
        }

        try {
            const response = await msalInstance.acquireTokenSilent({
                ...apiRequest,
                account: state.auth.account,
            });

            // Set the auth token for API requests
            setAuthToken(response.accessToken);

            return response.accessToken;
        } catch (error: any) {
            console.error('Token acquisition failed', error);

            // Try popup flow as fallback
            try {
                const response = await msalInstance.acquireTokenPopup({
                    ...apiRequest,
                    redirectUri: `${window.location.origin}/blank.html`,
                });

                // Set the auth token for API requests
                setAuthToken(response.accessToken);

                return response.accessToken;
            } catch (popupError: any) {
                console.error('Popup token acquisition failed', popupError);
                return rejectWithValue(popupError.message || 'Failed to acquire token');
            }
        }
    }
);

// Slice
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearAuthError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Initialize auth
        builder.addCase(initializeAuth.fulfilled, (state, action) => {
            if (action.payload) {
                state.isAuthenticated = true;
                state.account = action.payload.account;
                state.username = action.payload.username;
                state.accessToken = action.payload.accessToken;
            }
        });

        // Login
        builder.addCase(login.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            login.fulfilled,
            (state, action: PayloadAction<{
                account: AccountInfo | null;
                accessToken: string;
                username: string;
            } | null>) => {
                state.loading = false;

                if (action.payload) {
                    state.isAuthenticated = true;
                    state.account = action.payload.account;
                    state.username = action.payload.username;
                    state.accessToken = action.payload.accessToken;
                }
            }
        );
        builder.addCase(login.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Logout
        builder.addCase(logout.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(logout.fulfilled, (state) => {
            state.loading = false;
            state.isAuthenticated = false;
            state.account = null;
            state.username = null;
            state.accessToken = null;
        });
        builder.addCase(logout.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Acquire token
        builder.addCase(acquireToken.fulfilled, (state, action: PayloadAction<string>) => {
            state.accessToken = action.payload;
        });
    },
});

export const { clearAuthError } = authSlice.actions;

export default authSlice.reducer;